import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import MemeticGraphCanvas from '../components/MemeticGraphCanvas';
import MemeticConstellationCanvas from '../components/MemeticConstellationCanvas';
import MemeticStoryCanvas from '../components/MemeticStoryCanvas';
import {
  DEFAULT_EXPAND_PER_PARENT,
  DEFAULT_MIN_SALIENCE,
  DEFAULT_TOP_N,
  distillGraphData,
  buildBipartiteStoryGraph,
  ENTITY_TYPE_COLORS,
  ENTITY_TYPES,
  countNodesByEntityType,
  fetchArticleEntities,
  fetchSubgraphByMode,
  fetchTrendingEntities,
  formatEntityIdsParam,
  formatEntityTypesParam,
  formatExpandPerParentParam,
  formatTopNParam,
  LINK_KIND_OPTIONS,
  parseEntityIdsFromParam,
  parseEntityTypesParam,
  parseExpandPerParentParam,
  parseLayoutParam,
  parseLinkKindParam,
  parseMinSalienceParam,
  parseModeParam,
  parseRelationTypeParam,
  parseTopNParam,
  parseViewModeParam,
  rankNeighborhoodEntities,
  RELATION_TYPE_OPTIONS,
  searchGraphEntities,
  SPRAWL_OPTIONS,
  subgraphToForceGraph,
  toggleEntityType,
  TOP_N_OPTIONS,
} from '../services/graphApi';
import { DESK_CATEGORY_BY_ID, formatDeskTime } from './desk/deskConfig';
import { mapArticleToDeskColumn } from './desk/deskSearch';
import { formatArticleCategories } from '../utils/articleCategories';
import { WINDOW_OPTIONS, windowLabel, windowRange } from '../utils/window';
import { pluralize } from '../utils/pluralize';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePageMeta } from '../hooks/usePageMeta';
import { trackSearch, trackGraphView } from '../utils/analytics';
import SearchKittBar from '../components/SearchKittBar';
import './MemeticSpiderPage.css';

const DEPTH_OPTIONS = [1, 2, 3];

/** Short relation labels for narrow footer (MS-TOOLBAR-05). */
const RELATION_TYPE_SHORT = {
  CAUSAL: 'CAU',
  TEMPORAL: 'TMP',
  SUBEVENT: 'SUB',
  ROLE: 'ROL',
  SPEECH: 'SPK',
  ACTION: 'ACT',
  AFFILIATION: 'AFF',
  OTHER: 'OTH',
};

function buildSearchParams({
  q,
  depth,
  days,
  entityId,
  entityIds,
  mode = 'single',
  layout = 'starburst',
  view = 'entity',
  types,
  minSalience,
  linkKind = null,
  relationType = null,
  topN = DEFAULT_TOP_N,
  expandPerParent = DEFAULT_EXPAND_PER_PARENT,
  from = null,
  to = null,
}) {
  const params = {
    q: q || '',
    depth: String(depth),
    mode,
  };
  if (from && to) {
    params.from = from;
    params.to = to;
  } else {
    params.days = String(days);
  }
  const multi = formatEntityIdsParam(
    entityIds && entityIds.length > 0
      ? entityIds
      : (entityId ? [entityId] : []),
  );
  if (multi) {
    params.e = multi;
  }
  if (layout === 'constellation') {
    params.layout = 'constellation';
  }
  if (view === 'story') {
    params.view = 'story';
  }
  const sprawlParam = formatExpandPerParentParam(expandPerParent);
  if (sprawlParam) {
    params.expandPerParent = sprawlParam;
  }
  const typesParam = types ? formatEntityTypesParam(types) : null;
  if (typesParam) {
    params.types = typesParam;
  }
  if (minSalience != null && minSalience !== DEFAULT_MIN_SALIENCE) {
    params.minSalience = String(minSalience);
  }
  if (linkKind) {
    params.linkKind = linkKind;
  }
  if (relationType) {
    params.relationType = relationType;
  }
  const topNParam = formatTopNParam(topN);
  if (topNParam) {
    params.topN = topNParam;
  }
  return params;
}

const MAX_ENTITY_SEEDS = 8;

function isAddSeedClick(event) {
  return Boolean(event?.shiftKey || event?.metaKey || event?.ctrlKey);
}

function resolveUrlDays(searchParams) {
  if (searchParams.has('days')) {
    const parsed = Number(searchParams.get('days'));
    return parsed >= 1 ? parsed : 7;
  }
  if (searchParams.get('from') && searchParams.get('to')) {
    return null;
  }
  if (searchParams.get('q') || searchParams.get('e')) {
    return 7;
  }
  return 7;
}

function parseCustomRange(fromRaw, toRaw) {
  if (!fromRaw || !toRaw) {
    return null;
  }
  const from = new Date(fromRaw);
  const to = new Date(toRaw);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) {
    return { error: 'Invalid date range' };
  }
  if (from.getTime() >= to.getTime()) {
    return { error: 'Start must be before end' };
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

function toDateInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

const MemeticSpiderPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQuery = searchParams.get('q') || '';
  const urlDepth = Number(searchParams.get('depth')) || 2;
  const urlDays = resolveUrlDays(searchParams) ?? 7;
  const urlEntityIds = useMemo(
    () => parseEntityIdsFromParam(searchParams.get('e')),
    [searchParams],
  );
  const urlEntityId = urlEntityIds[0] || '';
  const fromArticle = searchParams.get('fromArticle') || '';
  const graphLayout = parseLayoutParam(searchParams.get('layout'));
  const viewMode = parseViewModeParam(searchParams.get('view'));
  const isStoryView = viewMode === 'story';
  const graphMode = parseModeParam(searchParams.get('mode'));
  const urlExpandPerParent = parseExpandPerParentParam(searchParams.get('expandPerParent'));
  const isConstellation = graphLayout === 'constellation';
  const selectedTypes = useMemo(
    () => parseEntityTypesParam(searchParams.get('types')),
    [searchParams],
  );
  const urlMinSalience = useMemo(
    () => parseMinSalienceParam(searchParams.get('minSalience')),
    [searchParams],
  );
  const urlLinkKind = useMemo(
    () => parseLinkKindParam(searchParams.get('linkKind')),
    [searchParams],
  );
  const urlRelationType = useMemo(
    () => parseRelationTypeParam(searchParams.get('relationType')),
    [searchParams],
  );
  const urlTopN = useMemo(
    () => parseTopNParam(searchParams.get('topN')),
    [searchParams],
  );
  const customRange = useMemo(
    () => parseCustomRange(searchParams.get('from'), searchParams.get('to')),
    [searchParams],
  );
  const hasCustomRange = Boolean(customRange && !customRange.error);

  useEffect(() => {
    if (hasCustomRange) {
      setWindowDatesOpen(true);
    }
  }, [hasCustomRange]);

  const [query, setQuery] = useState(urlQuery);
  const [suggestions, setSuggestions] = useState([]);
  const [subgraph, setSubgraph] = useState(null);
  const [rawGraphData, setRawGraphData] = useState({ nodes: [], links: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [centerId, setCenterId] = useState(null);
  const [seedIds, setSeedIds] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [trending, setTrending] = useState([]);
  const [trendingLoading, setTrendingLoading] = useState(false);
  // MS-EC-04b: open on desktop. MS-MOB-01: closed at phone widths so the canvas keeps the fold.
  const [timelineOpen, setTimelineOpen] = useState(() => (
    typeof window !== 'undefined' && window.matchMedia('(min-width: 993px)').matches
  ));
  const [focusArticleId, setFocusArticleId] = useState(null);
  const [windowDatesOpen, setWindowDatesOpen] = useState(false);
  const canvasWrapRef = useRef(null);
  const graphCanvasRef = useRef(null);
  const bootstrapRef = useRef(false);
  const fromArticleHandledRef = useRef(false);

  const reducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const timeWindow = useMemo(() => {
    if (hasCustomRange) {
      return { from: customRange.from, to: customRange.to };
    }
    return windowRange(urlDays);
  }, [hasCustomRange, customRange, urlDays]);
  const distillKey = `${formatEntityTypesParam(selectedTypes) || 'all'}|${urlMinSalience}|${urlLinkKind || 'all'}|${urlRelationType || 'all'}|${urlTopN || 'all'}`;
  const canvasLayoutKey = `${graphMode}|${urlQuery}|${urlDepth}|${urlDays}|${urlEntityIds.join(',')}|${graphLayout}|${viewMode}|${urlExpandPerParent}|${isFullscreen ? 'fs' : 'inline'}|${distillKey}|${hasCustomRange ? `${customRange.from}|${customRange.to}` : ''}`;

  const distilledGraphData = useMemo(
    () => distillGraphData(rawGraphData, {
      centerId,
      seedIds: seedIds.length > 0 ? seedIds : (centerId ? [centerId] : []),
      types: selectedTypes,
      minSalience: urlMinSalience,
      linkKind: urlLinkKind,
      relationType: urlRelationType,
      topN: urlTopN,
    }),
    [rawGraphData, centerId, seedIds, selectedTypes, urlMinSalience, urlLinkKind, urlRelationType, urlTopN],
  );

  const storyGraphData = useMemo(() => {
    if (!isStoryView) {
      return { nodes: [], links: [], stats: {} };
    }
    return buildBipartiteStoryGraph({
      entityNodes: distilledGraphData.nodes,
      articles: subgraph?.articles || [],
      topN: urlTopN,
    });
  }, [isStoryView, distilledGraphData.nodes, subgraph?.articles, urlTopN]);

  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  const loadSubgraph = useCallback(async () => {
    if (!urlQuery.trim() && urlEntityIds.length === 0 && !fromArticle) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchSubgraphByMode(graphMode, {
        q: urlEntityIds.length > 0 ? undefined : urlQuery.trim(),
        entityId: urlEntityIds.length === 0 ? (urlEntityId || undefined) : undefined,
        entityIds: urlEntityIds.length > 0 ? urlEntityIds : undefined,
        fromArticle: fromArticle || undefined,
        depth: urlDepth,
        expandPerParent: urlExpandPerParent,
        ...timeWindow,
      });
      setSubgraph(data);
      setRawGraphData(subgraphToForceGraph(data));
      const centers = (data.centers && data.centers.length > 0)
        ? data.centers.map((c) => c.id)
        : (data.center?.id ? [data.center.id] : urlEntityIds);
      setSeedIds(centers.filter(Boolean));
      setCenterId(centers[0] || urlEntityId || null);
    } catch (err) {
      setSubgraph(null);
      setRawGraphData({ nodes: [], links: [] });
      setCenterId(null);
      setSeedIds([]);
      setError(err.message || 'Failed to load subgraph');
    } finally {
      setLoading(false);
    }
  }, [urlQuery, urlEntityId, urlEntityIds, urlDepth, timeWindow, graphMode, urlExpandPerParent, fromArticle]);

  useEffect(() => {
    loadSubgraph();
  }, [loadSubgraph]);

  useEffect(() => {
    if (loading || distilledGraphData.nodes.length === 0) {
      return undefined;
    }
    const timers = [
      setTimeout(() => graphCanvasRef.current?.fitGraph(), 400),
      setTimeout(() => graphCanvasRef.current?.fitGraph(), 1200),
      setTimeout(() => graphCanvasRef.current?.fitGraph(), 2400),
    ];
    return () => timers.forEach(clearTimeout);
  }, [loading, canvasLayoutKey, distilledGraphData.nodes.length]);

  useEffect(() => {
    let cancelled = false;
    setTrendingLoading(true);
    fetchTrendingEntities({ limit: 8, ...timeWindow })
      .then((results) => {
        if (!cancelled) {
          setTrending(results);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTrending([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setTrendingLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [timeWindow]);

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(document.fullscreenElement === canvasWrapRef.current);
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = async () => {
    if (!canvasWrapRef.current) {
      return;
    }
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await canvasWrapRef.current.requestFullscreen();
      }
    } catch {
      // Browser blocked or unsupported — ignore
    }
  };

  usePageMeta({
    title: 'Memetic Web',
    path: '/web',
    description: 'Memetic Web — entity and story graphs across independent news sources.',
  });

  const debouncedQuery = useDebouncedValue(query, 250);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (!trimmed) {
      setSuggestions([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const results = await searchGraphEntities(trimmed, timeWindow);
        if (!cancelled) setSuggestions(results);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, timeWindow]);

  const applyControls = useCallback(({
    q,
    depth,
    days,
    entityId = null,
    entityIds = null,
    mode = graphMode,
    layout = graphLayout,
    view = viewMode,
    types = selectedTypes,
    minSalience = urlMinSalience,
    linkKind = urlLinkKind,
    relationType = urlRelationType,
    topN = urlTopN,
    expandPerParent = urlExpandPerParent,
    from = hasCustomRange ? customRange.from : null,
    to = hasCustomRange ? customRange.to : null,
  }) => {
    const params = buildSearchParams({
      q,
      depth,
      days,
      entityId,
      entityIds,
      mode,
      layout,
      view,
      types,
      minSalience,
      linkKind,
      relationType,
      topN,
      expandPerParent,
      from,
      to,
    });
    setSearchParams(params);
  }, [
    setSearchParams,
    graphMode,
    graphLayout,
    viewMode,
    selectedTypes,
    urlMinSalience,
    urlLinkKind,
    urlRelationType,
    urlTopN,
    urlExpandPerParent,
    hasCustomRange,
    customRange,
  ]);

  const applyDistill = useCallback((patch) => {
    applyControls({
      q: urlQuery,
      depth: urlDepth,
      days: urlDays,
      entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
      entityId: urlEntityIds.length === 0 ? urlEntityId : null,
      layout: graphLayout,
      ...patch,
    });
  }, [applyControls, urlQuery, urlDepth, urlDays, urlEntityIds, urlEntityId, graphLayout]);

  const applyEntitySelection = useCallback((entity, { addSeed = false } = {}) => {
    const id = String(entity?.id || '').trim();
    if (!id) {
      return;
    }
    const label = entity.label || entity.name || entity.slug || 'Entity';
    const slug = entity.slug || entity.label || entity.name || '';

    let nextIds;
    if (addSeed) {
      const base = urlEntityIds.length > 0
        ? [...urlEntityIds]
        : (centerId || urlEntityId ? [String(centerId || urlEntityId)] : []);
      nextIds = base.includes(id) ? base : [...base, id].slice(0, MAX_ENTITY_SEEDS);
    } else {
      nextIds = [id];
    }

    let nextMode = graphMode;
    if (!addSeed || nextIds.length <= 1) {
      nextMode = 'single';
    } else if (nextIds.length >= 2 && graphMode === 'single') {
      nextMode = 'union';
    } else if (nextIds.length < 2) {
      nextMode = 'single';
    }

    setQuery(label);
    applyControls({
      q: slug,
      depth: urlDepth,
      days: urlDays,
      entityIds: nextIds.length > 1 ? nextIds : null,
      entityId: nextIds.length === 1 ? nextIds[0] : null,
      layout: graphLayout,
      view: viewMode,
      mode: nextMode,
    });
  }, [
    applyControls,
    urlDepth,
    urlDays,
    urlEntityIds,
    centerId,
    urlEntityId,
    graphMode,
    graphLayout,
    viewMode,
  ]);

  // Legacy URL migration: mode, strip 3d render params
  useEffect(() => {
    const hasMode = searchParams.has('mode');
    const legacy3d = searchParams.get('view') === '3d' || searchParams.get('render') === '3d';
    const needsLegacyMode = !hasMode && (
      (parseLayoutParam(searchParams.get('layout')) === 'constellation'
        && parseEntityIdsFromParam(searchParams.get('e')).length > 1)
    );
    if (!needsLegacyMode && !legacy3d) {
      return;
    }
    const params = Object.fromEntries(searchParams.entries());
    delete params.render;
    if (params.view === '3d') {
      delete params.view;
    }
    if (needsLegacyMode) {
      params.mode = 'union';
    } else if (!hasMode) {
      params.mode = 'single';
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if ((graphMode === 'union' || graphMode === 'intersect')
        && urlEntityIds.length < 2
        && !fromArticle) {
      applyControls({
        q: urlQuery,
        depth: urlDepth,
        days: urlDays,
        entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
        entityId: urlEntityIds.length === 0 ? urlEntityId : null,
        mode: 'single',
      });
    }
  }, [graphMode, urlEntityIds.length, fromArticle, applyControls, urlQuery, urlDepth, urlDays, urlEntityIds, urlEntityId]);

  const setGraphMode = useCallback((nextMode) => {
    let mode = parseModeParam(nextMode);
    if ((mode === 'union' || mode === 'intersect') && urlEntityIds.length < 2) {
      return;
    }
    if (mode !== 'single' && urlEntityIds.length < 2) {
      mode = 'single';
    }
    applyControls({
      q: urlQuery,
      depth: urlDepth,
      days: urlDays,
      entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
      entityId: urlEntityIds.length === 0 ? urlEntityId : null,
      mode,
    });
  }, [applyControls, urlQuery, urlDepth, urlDays, urlEntityIds, urlEntityId]);

  const setGraphLayout = useCallback((nextLayout) => {
    applyControls({
      q: urlQuery,
      depth: urlDepth,
      days: urlDays,
      entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
      entityId: urlEntityIds.length === 0 ? urlEntityId : null,
      layout: nextLayout,
    });
  }, [applyControls, urlQuery, urlDepth, urlDays, urlEntityIds, urlEntityId]);

  const setViewMode = useCallback((nextView) => {
    applyControls({
      q: urlQuery,
      depth: urlDepth,
      days: urlDays,
      entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
      entityId: urlEntityIds.length === 0 ? urlEntityId : null,
      view: nextView,
    });
  }, [applyControls, urlQuery, urlDepth, urlDays, urlEntityIds, urlEntityId]);

  const setSprawl = useCallback((nextSprawl) => {
    applyControls({
      q: urlQuery,
      depth: urlDepth,
      days: urlDays,
      entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
      entityId: urlEntityIds.length === 0 ? urlEntityId : null,
      expandPerParent: nextSprawl,
    });
  }, [applyControls, urlQuery, urlDepth, urlDays, urlEntityIds, urlEntityId]);

  useEffect(() => {
    if (!fromArticle || urlQuery.trim() || urlEntityIds.length > 0 || fromArticleHandledRef.current) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const entities = await fetchArticleEntities(fromArticle, { limit: 5 });
        if (cancelled) {
          return;
        }
        fromArticleHandledRef.current = true;
        const list = Array.isArray(entities) ? entities.filter((entity) => entity?.id) : [];
        if (list.length > 0) {
          bootstrapRef.current = true;
          const multi = list.length > 1;
          let mode = graphMode;
          if (!multi) {
            mode = 'single';
          } else if (mode === 'single') {
            mode = 'union';
          }
          applyControls({
            q: list[0].slug || list[0].label,
            depth: urlDepth,
            days: urlDays,
            entityIds: list.map((entity) => entity.id),
            layout: graphLayout,
            mode,
          });
        }
      } catch (err) {
        if (!cancelled) {
          fromArticleHandledRef.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fromArticle, urlQuery, urlEntityIds, urlDepth, urlDays, applyControls, graphLayout, graphMode]);

  useEffect(() => {
    if (fromArticle && !fromArticleHandledRef.current) {
      return;
    }
    if (urlQuery.trim() || urlEntityIds.length > 0 || bootstrapRef.current || trendingLoading) {
      return;
    }
    if (trending.length === 0) {
      return;
    }
    bootstrapRef.current = true;
    const top = trending[0];
    applyControls({
      q: top.slug,
      depth: urlDepth,
      days: urlDays,
      entityId: top.id,
      layout: graphLayout,
    });
  }, [fromArticle, urlQuery, urlEntityIds, trending, trendingLoading, urlDepth, urlDays, applyControls, graphLayout]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (!query.trim()) {
      return;
    }
    applyControls({
      q: query.trim(),
      depth: urlDepth,
      days: urlDays,
      from: null,
      to: null,
      layout: graphLayout,
    });
    trackSearch(query.trim(), 0, 'web');
    trackGraphView(viewMode, query.trim());
  };

  const handleSuggestionPick = (entity, event) => {
    applyEntitySelection(entity, { addSeed: isAddSeedClick(event) });
  };

  const handleNodeClick = (node, event) => {
    if (!node?.id) {
      return;
    }
    if (node.nodeKind === 'article' || node.articleId) {
      navigate(`/article/${node.articleId || String(node.id).replace(/^article:/, '')}`);
      return;
    }
    applyEntitySelection(
      {
        id: node.id,
        name: node.name,
        label: node.name,
        slug: node.slug,
      },
      { addSeed: isAddSeedClick(event) },
    );
  };

  const handleTypeToggle = (type) => {
    applyDistill({
      types: toggleEntityType(selectedTypes, type),
      minSalience: urlMinSalience,
    });
  };

  const handleMinSalienceChange = (value) => {
    applyDistill({
      types: selectedTypes,
      minSalience: parseMinSalienceParam(value),
    });
  };

  const visibleEntityIds = useMemo(() => {
    const ids = new Set();
    (distilledGraphData.nodes || []).forEach((node) => {
      if (node?.id) {
        ids.add(String(node.id));
      }
    });
    if (centerId) {
      ids.add(String(centerId));
    }
    return ids;
  }, [distilledGraphData.nodes, centerId]);

  const articles = useMemo(() => {
    if (loading) {
      return [];
    }
    const all = subgraph?.articles || [];
    if (visibleEntityIds.size === 0) {
      return all;
    }
    // Only stories that mention an entity still on the distilled canvas.
    return all.filter((article) =>
      (article.matchedEntities || []).some((entityId) => visibleEntityIds.has(String(entityId))),
    );
  }, [loading, subgraph?.articles, visibleEntityIds]);

  const timelineArticles = useMemo(() => {
    return [...articles].sort((a, b) => {
      const ta = a.publishedAt ? Date.parse(a.publishedAt) : 0;
      const tb = b.publishedAt ? Date.parse(b.publishedAt) : 0;
      // Newest → oldest.
      return (Number.isFinite(tb) ? tb : 0) - (Number.isFinite(ta) ? ta : 0);
    });
  }, [articles]);

  /** Node ids actually painted by the active canvas (story view also renders article nodes). */
  const canvasNodeIds = useMemo(() => {
    if (!isStoryView) {
      return visibleEntityIds;
    }
    const ids = new Set();
    (storyGraphData.nodes || []).forEach((node) => {
      if (node?.id) {
        ids.add(String(node.id));
      }
    });
    return ids;
  }, [isStoryView, visibleEntityIds, storyGraphData.nodes]);

  /** Graph nodes for the story hovered in the Coverage timeline sidebar. */
  const focusNodeIds = useMemo(() => {
    if (focusArticleId == null) {
      return null;
    }
    const article = articles.find((item) => String(item.id) === String(focusArticleId));
    if (!article) {
      return null;
    }
    const candidates = [
      `article:${article.id}`,
      ...(article.matchedEntities || []).map((entityId) => String(entityId)),
    ];
    const ids = candidates.filter((id) => canvasNodeIds.has(id));
    return ids.length > 0 ? ids : null;
  }, [focusArticleId, articles, canvasNodeIds]);

  /** Hovered story whose entities were all pruned off the canvas — nothing to light up. */
  const focusOffGraph = focusArticleId != null && focusNodeIds == null;

  const nodeCount = isStoryView
    ? (storyGraphData.stats?.visibleArticles || 0) + (storyGraphData.stats?.visibleEntities || 0)
    : distilledGraphData.stats.visibleNodes;
  const totalNodeCount = isStoryView
    ? (storyGraphData.stats?.totalArticles || 0) + distilledGraphData.stats.totalNodes
    : distilledGraphData.stats.totalNodes;
  const edgeCount = isStoryView
    ? (storyGraphData.stats?.visibleLinks || 0)
    : distilledGraphData.stats.visibleLinks;
  const centerArticles = subgraph?.center?.articleCount ?? 0;
  const showResolvedHint = Boolean(
    subgraph?.center?.slug && urlQuery && subgraph.center.slug !== urlQuery && !loading,
  );
  const hasGraphSelection = Boolean(urlQuery.trim() || urlEntityIds.length > 0 || fromArticle);
  const feedbackPrimaryLine = loading
    ? 'Loading graph…'
    : !hasGraphSelection
      ? 'Search or pick an entity'
      : `${nodeCount} of ${totalNodeCount} ${isStoryView ? 'nodes' : 'entities'} · ${edgeCount} links`;
  const feedbackSecondaryLine = !loading && hasGraphSelection && isStoryView
    && storyGraphData.stats?.visibleArticles != null
    ? `${storyGraphData.stats.visibleArticles} stories visible`
    : !loading && hasGraphSelection && !showResolvedHint && centerArticles > 0
      ? `${centerArticles} center ${pluralize(centerArticles, 'article')} in ${windowLabel(urlDays)}`
      : null;
  const multiSeedReady = urlEntityIds.length >= 2;
  const applyGraphScope = useCallback(({ depth = urlDepth, days = urlDays, from, to } = {}) => {
    if (!(urlQuery.trim() || urlEntityIds.length > 0)) {
      return;
    }
    const range =
      from !== undefined || to !== undefined
        ? { from: from ?? null, to: to ?? null }
        : {};
    applyControls({
      q: urlQuery,
      depth,
      days,
      entityIds: urlEntityIds.length > 0 ? urlEntityIds : null,
      entityId: urlEntityIds.length === 0 ? urlEntityId : null,
      mode: graphMode,
      layout: graphLayout,
      expandPerParent: urlExpandPerParent,
      ...range,
    });
  }, [applyControls, urlQuery, urlEntityIds, urlEntityId, urlDepth, urlDays, graphLayout, graphMode, urlExpandPerParent]);

  const topEntityChips = useMemo(() => {
    if (hasGraphSelection && distilledGraphData.nodes.length > 0) {
      return rankNeighborhoodEntities(distilledGraphData.nodes, { limit: 8 });
    }
    return trending;
  }, [hasGraphSelection, distilledGraphData.nodes, trending]);
  const topEntitiesScoped = hasGraphSelection && distilledGraphData.nodes.length > 0;

  /** Non-default scope/distill params for MS-TOOLBAR-07 summary chips. */
  const activeFilterChips = useMemo(() => {
    if (!hasGraphSelection) {
      return [];
    }
    const chips = [];
    if (graphMode === 'union') {
      chips.push({
        id: 'mode-union',
        tier: 'global',
        label: 'Union',
        onClear: () => setGraphMode('single'),
      });
    } else if (graphMode === 'intersect') {
      chips.push({
        id: 'mode-intersect',
        tier: 'global',
        label: 'Intersect',
        onClear: () => setGraphMode('single'),
      });
    }
    if (isStoryView) {
      chips.push({
        id: 'view-story',
        tier: 'story',
        label: 'Story view',
        onClear: () => setViewMode('entity'),
      });
    }
    if (isConstellation) {
      chips.push({
        id: 'layout-constellation',
        tier: 'story',
        label: 'Constellation',
        onClear: () => setGraphLayout('starburst'),
      });
    }
    if (urlDepth !== 2) {
      chips.push({
        id: 'depth',
        tier: 'global',
        label: `Depth ${urlDepth}`,
        onClear: () => applyGraphScope({ depth: 2 }),
      });
    }
    if (hasCustomRange) {
      chips.push({
        id: 'custom-range',
        tier: 'global',
        label: 'Custom dates',
        onClear: () => applyGraphScope({ days: urlDays, from: null, to: null }),
      });
    } else if (urlDays !== 7) {
      chips.push({
        id: 'window',
        tier: 'global',
        label: windowLabel(urlDays),
        onClear: () => applyGraphScope({ days: 7, from: null, to: null }),
      });
    }
    if (urlExpandPerParent !== DEFAULT_EXPAND_PER_PARENT) {
      chips.push({
        id: 'sprawl',
        tier: 'global',
        label: urlExpandPerParent === 0 ? 'Sprawl All' : `Sprawl ${urlExpandPerParent}`,
        onClear: () => setSprawl(DEFAULT_EXPAND_PER_PARENT),
      });
    }
    if (selectedTypes.size !== ENTITY_TYPES.length) {
      const hiddenCount = ENTITY_TYPES.length - selectedTypes.size;
      chips.push({
        id: 'types',
        tier: 'entity',
        label: `${hiddenCount} type${hiddenCount === 1 ? '' : 's'} hidden`,
        onClear: () => applyDistill({ types: new Set(ENTITY_TYPES) }),
      });
    }
    if (urlMinSalience !== DEFAULT_MIN_SALIENCE) {
      chips.push({
        id: 'salience',
        tier: 'entity',
        label: `Min ${urlMinSalience.toFixed(2)}`,
        onClear: () => applyDistill({ minSalience: DEFAULT_MIN_SALIENCE }),
      });
    }
    if (urlLinkKind != null) {
      chips.push({
        id: 'linkKind',
        tier: 'entity',
        label: urlLinkKind === 'CO_MENTION' ? 'Co-mention' : 'Related',
        onClear: () => applyDistill({ linkKind: null }),
      });
    }
    if (urlRelationType != null) {
      chips.push({
        id: 'relationType',
        tier: 'entity',
        label: urlRelationType,
        onClear: () => applyDistill({ relationType: null }),
      });
    }
    if (urlTopN == null) {
      chips.push({
        id: 'topN',
        tier: 'entity',
        label: 'Density All',
        onClear: () => applyDistill({ topN: DEFAULT_TOP_N }),
      });
    } else if (urlTopN !== DEFAULT_TOP_N) {
      chips.push({
        id: 'topN',
        tier: 'entity',
        label: `Top ${urlTopN}`,
        onClear: () => applyDistill({ topN: DEFAULT_TOP_N }),
      });
    }
    return chips;
  }, [
    hasGraphSelection,
    graphMode,
    isStoryView,
    isConstellation,
    urlDepth,
    hasCustomRange,
    urlDays,
    urlExpandPerParent,
    selectedTypes,
    urlMinSalience,
    urlLinkKind,
    urlRelationType,
    urlTopN,
    setGraphMode,
    setViewMode,
    setGraphLayout,
    applyGraphScope,
    setSprawl,
    applyDistill,
  ]);

  const nodeMetaById = useMemo(() => {
    const map = new Map();
    (distilledGraphData.nodes || []).forEach((node) => {
      if (node?.id) {
        map.set(String(node.id), {
          label: node.name || node.label || 'Entity',
          articleCount: node.articleCount ?? 0,
        });
      }
    });
    return map;
  }, [distilledGraphData.nodes]);
  const activeSeedIds = useMemo(() => (
    urlEntityIds.length > 0
      ? urlEntityIds
      : (centerId ? [String(centerId)] : [])
  ), [urlEntityIds, centerId]);
  const seedAnchors = useMemo(() => {
    const centerMap = new Map();
    (subgraph?.centers || []).forEach((center) => {
      centerMap.set(String(center.id), center);
    });
    if (subgraph?.center?.id) {
      centerMap.set(String(subgraph.center.id), subgraph.center);
    }
    return activeSeedIds.map((id) => {
      const known = centerMap.get(String(id));
      const meta = nodeMetaById.get(String(id));
      return {
        id: String(id),
        label: known?.label || meta?.label || 'Entity',
        slug: known?.slug,
        articleCount: known?.articleCount ?? meta?.articleCount ?? 0,
      };
    });
  }, [activeSeedIds, subgraph?.centers, subgraph?.center, nodeMetaById]);
  const handleRemoveSeed = useCallback((seedId) => {
    const id = String(seedId);
    const explicitIds = urlEntityIds.length > 0
      ? urlEntityIds
      : (centerId ? [String(centerId)] : []);
    const nextIds = explicitIds.filter((seed) => seed !== id);

    if (nextIds.length === 0) {
      setQuery('');
      applyControls({
        q: '',
        depth: urlDepth,
        days: urlDays,
        entityIds: null,
        entityId: null,
        mode: 'single',
        layout: graphLayout,
        view: viewMode,
      });
      return;
    }

    let nextMode = graphMode;
    if (nextIds.length < 2) {
      nextMode = 'single';
    }

    const centerMap = new Map();
    (subgraph?.centers || []).forEach((center) => {
      centerMap.set(String(center.id), center);
    });
    if (subgraph?.center?.id) {
      centerMap.set(String(subgraph.center.id), subgraph.center);
    }
    const primary = centerMap.get(nextIds[0]);
    const primaryLabel = primary?.label || nodeMetaById.get(nextIds[0])?.label || '';
    setQuery(primaryLabel);
    applyControls({
      q: primary?.slug || primaryLabel,
      depth: urlDepth,
      days: urlDays,
      entityIds: nextIds.length > 1 ? nextIds : null,
      entityId: nextIds.length === 1 ? nextIds[0] : null,
      mode: nextMode,
      layout: graphLayout,
      view: viewMode,
    });
  }, [
    urlEntityIds,
    centerId,
    graphMode,
    urlDepth,
    urlDays,
    graphLayout,
    viewMode,
    applyControls,
    subgraph?.centers,
    subgraph?.center,
    nodeMetaById,
  ]);
  const entityTypeCounts = useMemo(() => {
    if (isStoryView) {
      return countNodesByEntityType(storyGraphData.nodes || [], { excludeTypes: ['ARTICLE'] });
    }
    return countNodesByEntityType(distilledGraphData.nodes || []);
  }, [isStoryView, storyGraphData.nodes, distilledGraphData.nodes]);
  const storyArticleCount = useMemo(() => {
    if (!isStoryView) {
      return 0;
    }
    return (storyGraphData.nodes || []).filter((node) => node.type === 'ARTICLE').length;
  }, [isStoryView, storyGraphData.nodes]);
  const richerSuggestion = suggestions.find(
    (entity) => entity.articleCount > centerArticles && entity.id !== subgraph?.center?.id,
  );

  return (
    <div className="reader-page memetic-spider-page">
      <div className="container memetic-spider-shell">
        <div className="memetic-workspace">
        <form className="memetic-controls memetic-workspace-controls" onSubmit={handleSearchSubmit}>
          <div className="memetic-control-group memetic-control-search">
            <label htmlFor="memetic-search">Entity search</label>
            <input
              id="memetic-search"
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search entities…"
              autoComplete="off"
            />
            {suggestions.length > 0 && query.trim() && (
              <ul className="memetic-suggestions" role="listbox">
                {suggestions.slice(0, 8).map((entity) => (
                  <li key={entity.id}>
                    <button type="button" onClick={(event) => handleSuggestionPick(entity, event)}>
                      <span>{entity.label}</span>
                      <span className="memetic-suggestion-meta">
                        {entity.type} · {entity.articleCount} {pluralize(entity.articleCount, 'article')} · {windowLabel(urlDays)}
                      </span>
                    </button>
                    {entity.slug && (
                      <Link
                        to={`/entity/${entity.slug}`}
                        className="memetic-suggestion-timeline"
                        onClick={(event) => event.stopPropagation()}
                      >
                        Timeline
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div
            className="memetic-control-group memetic-control-feedback"
            role="status"
            aria-live="polite"
            aria-busy={loading}
          >
            <span className="memetic-control-label">Showing</span>
            <div className="memetic-feedback-stack">
              <span className="memetic-feedback-line memetic-feedback-line--primary">
                {feedbackPrimaryLine}
              </span>
              <span
                className={`memetic-feedback-line${feedbackSecondaryLine ? '' : ' memetic-feedback-line--reserved'}`}
                aria-hidden={!feedbackSecondaryLine}
              >
                {feedbackSecondaryLine || '\u00a0'}
              </span>
              <span
                className={`memetic-feedback-line memetic-feedback-line--detail${showResolvedHint ? '' : ' memetic-feedback-line--reserved'}`}
                aria-hidden={!showResolvedHint}
              >
                {showResolvedHint ? (
                  <>
                    Resolved <strong>{subgraph.center.label}</strong>
                    {' '}({centerArticles} {pluralize(centerArticles, 'article')} in {windowLabel(urlDays)})
                    {' for “'}{urlQuery}{'”'}
                    {' · '}
                    <Link to={`/entity/${subgraph.center.slug}`} className="memetic-entity-hub-link">
                      Entity timeline
                    </Link>
                  </>
                ) : '\u00a0'}
              </span>
            </div>
          </div>
          <div className="memetic-control-group memetic-control-live">
            <span className="memetic-control-label" aria-hidden="true">&nbsp;</span>
            <div className="memetic-demo-badge" role="status">
              <span className="memetic-demo-dot memetic-demo-dot-live" aria-hidden="true" />
              Live graph
            </div>
          </div>
        </form>
        {hasGraphSelection && seedAnchors.length > 0 && (
          <section className="memetic-seed-anchors" aria-label="Graph seed anchors">
            <span className="memetic-seed-anchors-label">Seeds</span>
            <div className="memetic-seed-anchors-chips" role="list">
              {seedAnchors.map((seed) => (
                <span key={seed.id} className="memetic-seed-chip" role="listitem">
                  <span className="memetic-seed-chip-label">{seed.label}</span>
                  <span className="memetic-trending-count">{seed.articleCount}</span>
                  <button
                    type="button"
                    className="memetic-seed-chip-remove"
                    aria-label={`Remove ${seed.label} from seeds`}
                    disabled={loading}
                    onClick={() => handleRemoveSeed(seed.id)}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            {!multiSeedReady && (
              <span className="memetic-seed-anchors-hint">
                Shift+click graph or chips to add another seed
              </span>
            )}
          </section>
        )}
        {(topEntityChips.length > 0 || trendingLoading) && (
          <section className="memetic-trending" aria-label="Top entities in time window">
            <span className="memetic-trending-label">
              {topEntitiesScoped
                ? 'Neighbors in this graph'
                : (urlDays === 1 ? 'Entities of the day' : `Top entities · ${windowLabel(urlDays)}`)}
            </span>
            <div className="memetic-trending-chips" role="group" aria-label="Top entities">
              {trendingLoading && topEntityChips.length === 0 && (
                <span className="memetic-trending-loading">Loading…</span>
              )}
              {topEntityChips.map((entity) => {
                const active = urlEntityIds.includes(String(entity.id))
                  || urlEntityId === entity.id
                  || urlQuery === entity.slug;
                const isSeed = activeSeedIds.includes(String(entity.id));
                return (
                  <button
                    key={entity.id}
                    type="button"
                    className={`memetic-trending-chip${active ? ' active' : ''}${isSeed ? ' memetic-trending-chip--seed' : ''}`}
                    aria-pressed={active}
                    title={isSeed ? 'Seed anchor · Shift+click to add another' : 'Focus · Shift+click to add seed'}
                    onClick={(event) => handleSuggestionPick(entity, event)}
                  >
                    <span>{entity.label}</span>
                    <span className="memetic-trending-count">{entity.articleCount}</span>
                  </button>
                );
              })}
            </div>
          </section>
        )}
        <SearchKittBar active={loading} label="Loading graph" />

        {activeFilterChips.length > 0 && (
          <div className="memetic-active-filters" aria-label="Active filters">
            <span className="memetic-active-filters-label">Active</span>
            {activeFilterChips.map((chip) => (
              <button
                key={chip.id}
                type="button"
                className={`memetic-active-filter memetic-active-filter--${chip.tier}`}
                title={
                  chip.tier === 'global'
                    ? 'Global — refetches graph'
                    : chip.tier === 'entity'
                      ? 'Entity — client distill (no refetch)'
                      : 'Story — view layer'
                }
                disabled={loading}
                onClick={chip.onClear}
              >
                {chip.label}
                <span className="memetic-active-filter-clear" aria-hidden="true">×</span>
              </button>
            ))}
          </div>
        )}

        <div className="memetic-workspace-main">
        {error && <div className="memetic-error" role="alert">{error}</div>}

        {subgraph && nodeCount <= 1 && edgeCount === 0 && !loading && (
          <div className="memetic-sparse-hint" role="status">
            Few connections in {windowLabel(urlDays)}
            {richerSuggestion ? (
              <>
                {' '}— try{' '}
                <button
                  type="button"
                  className="memetic-sparse-link"
                  onClick={() => handleSuggestionPick(richerSuggestion)}
                >
                  {richerSuggestion.label}
                </button>
                {' '}({richerSuggestion.articleCount} {pluralize(richerSuggestion.articleCount, 'article')}) or widen to <strong>7d</strong>.
              </>
            ) : (
              <> — widen to <strong>7d</strong> or pick a suggestion with more articles.</>
            )}
          </div>
        )}

        <div className="memetic-graph-row">
        <section
          className={`memetic-canvas-wrap${isFullscreen ? ' memetic-canvas-wrap--fullscreen' : ''}`}
          aria-label="Memetic graph canvas"
          ref={canvasWrapRef}
        >
          <div className="memetic-canvas-toolbar">
            <div className="memetic-canvas-toolbar-actions">
              <div className="memetic-toolbar-group" role="group" aria-label="Graph search mode">
                <div className="memetic-view-toggle">
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${graphMode === 'single' ? ' active' : ''}`}
                    aria-pressed={graphMode === 'single'}
                    disabled={!hasGraphSelection}
                    onClick={() => setGraphMode('single')}
                  >
                    Single
                  </button>
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${graphMode === 'union' ? ' active' : ''}`}
                    aria-pressed={graphMode === 'union'}
                    disabled={!hasGraphSelection || !multiSeedReady}
                    title={multiSeedReady ? 'Union explore' : 'Add another seed'}
                    onClick={() => setGraphMode('union')}
                  >
                    Union
                  </button>
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${graphMode === 'intersect' ? ' active' : ''}`}
                    aria-pressed={graphMode === 'intersect'}
                    disabled={!hasGraphSelection || !multiSeedReady}
                    title={multiSeedReady ? 'Intersect connect' : 'Add another seed'}
                    onClick={() => setGraphMode('intersect')}
                  >
                    Intersect
                  </button>
                </div>
              </div>
              <div className="memetic-toolbar-group" role="group" aria-label="Graph content view">
                <div className="memetic-view-toggle">
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${!isStoryView ? ' active' : ''}`}
                    aria-pressed={!isStoryView}
                    onClick={() => setViewMode('entity')}
                  >
                    Entity
                  </button>
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${isStoryView ? ' active' : ''}`}
                    aria-pressed={isStoryView}
                    disabled={!hasGraphSelection}
                    onClick={() => setViewMode('story')}
                  >
                    Story
                  </button>
                </div>
              </div>
              <div className="memetic-toolbar-group" role="group" aria-label="Graph layout">
                <div className="memetic-view-toggle">
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${!isConstellation ? ' active' : ''}`}
                    aria-pressed={!isConstellation}
                    onClick={() => setGraphLayout('starburst')}
                  >
                    Starburst
                  </button>
                  <button
                    type="button"
                    className={`memetic-view-toggle-btn${isConstellation ? ' active' : ''}`}
                    aria-pressed={isConstellation}
                    onClick={() => setGraphLayout('constellation')}
                  >
                    Constellation
                  </button>
                </div>
              </div>
              <div className="memetic-toolbar-group memetic-toolbar-group--global" role="group" aria-label="Graph scope">
                <div className="memetic-toolbar-scope" role="group" aria-label="Hop depth">
                  <span className="memetic-toolbar-scope-label">Depth</span>
                  <div className="memetic-view-toggle">
                    {DEPTH_OPTIONS.map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`memetic-view-toggle-btn${urlDepth === value ? ' active' : ''}`}
                        aria-pressed={urlDepth === value}
                        disabled={loading || !hasGraphSelection}
                        onClick={() => applyGraphScope({ depth: value })}
                      >
                        {value}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="memetic-toolbar-scope memetic-toolbar-window" role="group" aria-label="Time window">
                  <span className="memetic-toolbar-scope-label">Window</span>
                  <div className="memetic-window-control">
                    <div className="memetic-view-toggle">
                      {WINDOW_OPTIONS.map((option) => (
                        <button
                          key={option.label}
                          type="button"
                          className={`memetic-view-toggle-btn${!hasCustomRange && urlDays === option.days ? ' active' : ''}`}
                          aria-pressed={!hasCustomRange && urlDays === option.days}
                          disabled={loading || !hasGraphSelection}
                          onClick={() => applyGraphScope({ days: option.days, from: null, to: null })}
                        >
                          {option.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        className={`memetic-view-toggle-btn memetic-window-dates-toggle${windowDatesOpen || hasCustomRange ? ' active' : ''}`}
                        aria-pressed={windowDatesOpen || hasCustomRange}
                        aria-expanded={windowDatesOpen || hasCustomRange}
                        disabled={loading || !hasGraphSelection}
                        onClick={() => setWindowDatesOpen((open) => !open)}
                      >
                        Dates
                      </button>
                    </div>
                    <div
                      className={[
                        'memetic-custom-range',
                        'memetic-window-dates',
                        hasCustomRange ? 'is-active' : '',
                        windowDatesOpen ? 'is-open' : '',
                      ].filter(Boolean).join(' ')}
                      aria-label="Custom date range"
                    >
                      <input
                        type="date"
                        aria-label="From date"
                        disabled={loading || !hasGraphSelection}
                        value={hasCustomRange ? toDateInputValue(customRange.from) : ''}
                        onChange={(event) => {
                          if (!hasGraphSelection) return;
                          const nextFrom = event.target.value
                            ? new Date(`${event.target.value}T00:00:00.000Z`).toISOString()
                            : null;
                          const nextTo = hasCustomRange
                            ? customRange.to
                            : new Date().toISOString();
                          if (!nextFrom) return;
                          applyGraphScope({ from: nextFrom, to: nextTo });
                        }}
                      />
                      <span className="memetic-custom-range-sep">→</span>
                      <input
                        type="date"
                        aria-label="To date"
                        disabled={loading || !hasGraphSelection}
                        value={hasCustomRange ? toDateInputValue(customRange.to) : ''}
                        onChange={(event) => {
                          if (!hasGraphSelection) return;
                          const nextTo = event.target.value
                            ? new Date(`${event.target.value}T23:59:59.999Z`).toISOString()
                            : null;
                          const nextFrom = hasCustomRange
                            ? customRange.from
                            : windowRange(urlDays).from;
                          if (!nextTo) return;
                          applyGraphScope({ from: nextFrom, to: nextTo });
                        }}
                      />
                    </div>
                    {customRange?.error && (
                      <span className="memetic-range-error" role="alert">{customRange.error}</span>
                    )}
                  </div>
                </div>
                <div className="memetic-toolbar-scope" role="group" aria-label="Sprawl cap per node">
                  <span className="memetic-toolbar-scope-label">Sprawl</span>
                  <div className="memetic-view-toggle">
                    {SPRAWL_OPTIONS.map((value) => (
                      <button
                        key={value}
                        type="button"
                        className={`memetic-view-toggle-btn${urlExpandPerParent === value ? ' active' : ''}`}
                        aria-pressed={urlExpandPerParent === value}
                        disabled={loading || !hasGraphSelection}
                        onClick={() => setSprawl(value)}
                      >
                        {value}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`memetic-view-toggle-btn${urlExpandPerParent === 0 ? ' active' : ''}`}
                      aria-pressed={urlExpandPerParent === 0}
                      disabled={loading || !hasGraphSelection}
                      onClick={() => setSprawl(0)}
                    >
                      All
                    </button>
                  </div>
                </div>
              </div>
              <div className="memetic-toolbar-group memetic-toolbar-group--entity" role="group" aria-label="Entity distill">
                <div className="memetic-toolbar-scope" role="group" aria-label="Top-N neighbors">
                  <span className="memetic-toolbar-scope-label">Density</span>
                  <div className="memetic-view-toggle">
                    {TOP_N_OPTIONS.map((n) => (
                      <button
                        key={n}
                        type="button"
                        className={`memetic-view-toggle-btn${urlTopN === n ? ' active' : ''}`}
                        aria-pressed={urlTopN === n}
                        disabled={loading || !hasGraphSelection}
                        onClick={() => applyDistill({ topN: n })}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      type="button"
                      className={`memetic-view-toggle-btn${urlTopN == null ? ' active' : ''}`}
                      aria-pressed={urlTopN == null}
                      disabled={loading || !hasGraphSelection}
                      onClick={() => applyDistill({ topN: null })}
                    >
                      All
                    </button>
                  </div>
                </div>
                <div className="memetic-toolbar-scope" role="group" aria-label="Link kind">
                  <span className="memetic-toolbar-scope-label">Links</span>
                  <div className="memetic-view-toggle">
                    <button
                      type="button"
                      className={`memetic-view-toggle-btn${urlLinkKind == null ? ' active' : ''}`}
                      aria-pressed={urlLinkKind == null}
                      disabled={loading || !hasGraphSelection}
                      onClick={() => applyDistill({ linkKind: null })}
                    >
                      All
                    </button>
                    {LINK_KIND_OPTIONS.map((kind) => (
                      <button
                        key={kind}
                        type="button"
                        className={`memetic-view-toggle-btn${urlLinkKind === kind ? ' active' : ''}`}
                        aria-pressed={urlLinkKind === kind}
                        disabled={loading || !hasGraphSelection}
                        onClick={() => applyDistill({ linkKind: kind })}
                      >
                        {kind === 'CO_MENTION' ? 'Co-mention' : 'Related'}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="memetic-canvas-toolbar-screen" role="group" aria-label="Screen">
              <span className="memetic-mobile-graph-hint">Landscape or full screen for more room</span>
              <button
                type="button"
                className="memetic-fullscreen-btn"
                onClick={toggleFullscreen}
                aria-pressed={isFullscreen}
                aria-label={isFullscreen ? 'Exit full screen graph' : 'Full screen graph'}
              >
                {isFullscreen ? 'Exit full screen' : 'Full screen'}
              </button>
            </div>
          </div>

          <div className="memetic-canvas-body">
            <div className="memetic-canvas-stage">
              {isStoryView ? (
                <MemeticStoryCanvas
                  ref={graphCanvasRef}
                  graphData={storyGraphData}
                  onNodeClick={handleNodeClick}
                  reducedMotion={reducedMotion}
                  layoutKey={canvasLayoutKey}
                  focusNodeIds={focusNodeIds}
                  seedIds={seedIds}
                  layout={graphLayout}
                />
              ) : isConstellation ? (
                <MemeticConstellationCanvas
                  ref={graphCanvasRef}
                  graphData={distilledGraphData}
                  seedIds={seedIds}
                  onNodeClick={handleNodeClick}
                  reducedMotion={reducedMotion}
                  layoutKey={canvasLayoutKey}
                  focusNodeIds={focusNodeIds}
                />
              ) : (
                <MemeticGraphCanvas
                  ref={graphCanvasRef}
                  graphData={distilledGraphData}
                  centerId={centerId}
                  seedIds={seedIds}
                  onNodeClick={handleNodeClick}
                  reducedMotion={reducedMotion}
                  layoutKey={canvasLayoutKey}
                  focusNodeIds={focusNodeIds}
                />
              )}
            </div>
            {hasGraphSelection && (
              <aside
                className={`memetic-timeline-drawer${timelineOpen ? ' open' : ''}`}
                aria-label="Coverage timeline"
              >
                <button
                  type="button"
                  className="memetic-timeline-rail"
                  onClick={() => setTimelineOpen((open) => !open)}
                  aria-expanded={timelineOpen}
                  title={timelineOpen ? 'Collapse coverage timeline' : 'Expand coverage timeline'}
                >
                  <span className="memetic-timeline-rail-label">TIMELINE</span>
                  {timelineArticles.length > 0 && (
                    <span className="memetic-timeline-count">{timelineArticles.length}</span>
                  )}
                  <span className="memetic-timeline-rail-caret" aria-hidden="true">
                    {timelineOpen ? '▸' : '◂'}
                  </span>
                </button>
                <div className="memetic-timeline-panel">
                  <header className="memetic-timeline-overlay-header">
                    <h2>Coverage timeline</h2>
                    <p>
                      Newest → oldest on the visible graph · hover a story to light its node · edges are <strong>asserted in source</strong>
                    </p>
                  </header>
                  <ol className="memetic-timeline-overlay-list">
                    {timelineArticles.length === 0 && (
                      <li className="memetic-timeline-overlay-empty">
                        No dated stories on the distilled graph in this window.
                      </li>
                    )}
                    {timelineArticles.slice(0, 40).map((article) => {
                      const focused = focusArticleId === article.id;
                      const offGraph = focused && focusOffGraph;
                      return (
                        <li
                          key={article.id}
                          className={[
                            focused ? 'is-focused' : '',
                            offGraph ? 'is-off-graph' : '',
                          ].filter(Boolean).join(' ') || undefined}
                          onMouseEnter={() => setFocusArticleId(article.id)}
                          onMouseLeave={() => setFocusArticleId((current) => (current === article.id ? null : current))}
                          onFocus={() => setFocusArticleId(article.id)}
                          onBlur={() => setFocusArticleId((current) => (current === article.id ? null : current))}
                        >
                          <time dateTime={article.publishedAt || undefined}>
                            {formatDeskTime(article.publishedAt)}
                          </time>
                          <Link to={`/article/${article.id}`}>{article.title}</Link>
                          {article.source && <span className="memetic-timeline-overlay-source">{article.source}</span>}
                          {offGraph && (
                            <span className="memetic-timeline-overlay-hint">
                              No node on the canvas — its entities were pruned by Density or the type/salience filters.
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </aside>
            )}
          </div>

          <div className="memetic-graph-footer" aria-label="Graph distill controls">
            <div className="memetic-graph-footer-row memetic-graph-footer-row--types" aria-label="Entity type filter">
              {isStoryView && (
                <span className="memetic-legend-type">
                  <i className="memetic-dot memetic-node-article" />
                  {' ARTICLE '}
                  <span className="memetic-legend-count">{storyArticleCount}</span>
                </span>
              )}
              {ENTITY_TYPES.map((type) => {
                const active = selectedTypes.has(type);
                const typeCount = entityTypeCounts.get(type) ?? 0;
                return (
                  <button
                    key={type}
                    type="button"
                    className={`memetic-legend-type${active ? ' is-active' : ' is-dim'}`}
                    aria-pressed={active}
                    disabled={loading || !hasGraphSelection}
                    onClick={() => handleTypeToggle(type)}
                    style={{ '--memetic-type-color': ENTITY_TYPE_COLORS[type] }}
                  >
                    <i className="memetic-dot" style={{ background: ENTITY_TYPE_COLORS[type] }} />
                    {type}
                    <span className="memetic-legend-count">{typeCount}</span>
                  </button>
                );
              })}
              <div
                className={`memetic-footer-salience${urlMinSalience !== DEFAULT_MIN_SALIENCE ? ' is-active' : ''}`}
              >
                <label htmlFor="memetic-min-salience-footer" className="memetic-footer-salience-label">
                  Relevance · {urlMinSalience.toFixed(2)}
                </label>
                <input
                  id="memetic-min-salience-footer"
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={urlMinSalience}
                  disabled={loading || !hasGraphSelection}
                  onChange={(event) => handleMinSalienceChange(event.target.value)}
                  aria-label="Minimum relevance"
                />
              </div>
            </div>
            {!isStoryView && (
              <div
                className="memetic-graph-footer-row memetic-graph-footer-row--relations"
                role="group"
                aria-label="Relation type filter"
              >
                <span className="memetic-footer-relations-label">Relations</span>
                <div className="memetic-footer-relations-pills">
                  <button
                    type="button"
                    className={`memetic-pill${urlRelationType == null ? ' active' : ''}`}
                    aria-pressed={urlRelationType == null}
                    disabled={loading || !hasGraphSelection}
                    onClick={() => applyDistill({ relationType: null })}
                  >
                    All
                  </button>
                  {RELATION_TYPE_OPTIONS.map((type) => (
                    <button
                      key={type}
                      type="button"
                      className={`memetic-pill${urlRelationType === type ? ' active' : ''}`}
                      aria-pressed={urlRelationType === type}
                      disabled={loading || !hasGraphSelection}
                      onClick={() => applyDistill({ relationType: type })}
                      title={type}
                    >
                      <span className="memetic-pill-label-full">{type}</span>
                      <span className="memetic-pill-label-short">{RELATION_TYPE_SHORT[type] || type.slice(0, 3)}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
        </div>
        </div>
        </div>

        <section
          className="memetic-articles-panel"
          aria-label="Related articles"
          aria-busy={loading}
        >
          <header className="memetic-articles-header">
            <div>
              <h2>Stories behind this graph</h2>
              <p className="memetic-articles-subtitle">
                {subgraph?.center?.label ? (
                  <>
                    <strong>{subgraph.center.label}</strong> and its closest connections · {windowLabel(urlDays)}
                    {loading ? ' · loading…' : ` · ${articles.length} shown`}
                  </>
                ) : (
                  <>
                    Closest connections · {windowLabel(urlDays)}
                    {loading ? ' · loading…' : ` · ${articles.length} shown`}
                  </>
                )}
              </p>
            </div>
            <span className="memetic-panel-badge">Live</span>
          </header>
          <ul className="memetic-article-list" key={`${urlEntityId || urlQuery}-${urlDepth}-${urlDays}`}>
            {loading && articles.length === 0 && (
              <li className="memetic-article-row memetic-article-empty">Loading articles…</li>
            )}
            {!loading && articles.length === 0 && (
              <li className="memetic-article-row memetic-article-empty">
                No stories on the visible graph in {windowLabel(urlDays)}.
              </li>
            )}
            {articles.map((article) => {
              const deskId = mapArticleToDeskColumn(article);
              const cats = formatArticleCategories(article);
              const source = article.source || 'Unknown source';
              const visibleMatched = (article.matchedEntities || []).filter((entityId) =>
                visibleEntityIds.has(String(entityId)),
              );
              return (
              <li key={article.id} className="memetic-article-row">
                <div className="memetic-article-body">
                  <Link to={`/article/${article.id}`} className="memetic-article-title memetic-article-title-link">
                    {article.title}
                  </Link>
                  <div className="memetic-article-meta">
                    <span className="memetic-article-source">{source}</span>
                    <span className="memetic-article-time">{formatDeskTime(article.publishedAt)}</span>
                    {(cats || deskId) && (
                      <span
                        className="memetic-article-category"
                        style={{
                          '--memetic-cat-accent': deskId && DESK_CATEGORY_BY_ID[deskId]?.accentVar
                            ? `var(${DESK_CATEGORY_BY_ID[deskId].accentVar})`
                            : undefined,
                        }}
                      >
                        {cats || DESK_CATEGORY_BY_ID[deskId]?.shortLabel}
                      </span>
                    )}
                  </div>
                  {visibleMatched.length > 0 && (
                    <div className="memetic-article-entities" aria-label="Entities in subgraph">
                      {visibleMatched.slice(0, 4).map((entityId) => (
                        <button
                          key={entityId}
                          type="button"
                          className="memetic-article-entity-chip"
                          onClick={() => {
                            const label = nodeMetaById.get(String(entityId))?.label || 'Entity';
                            applyControls({
                              q: label,
                              depth: urlDepth,
                              days: urlDays,
                              entityId: String(entityId),
                            });
                          }}
                        >
                          {nodeMetaById.get(String(entityId))?.label || 'Entity'}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </li>
              );
            })}
          </ul>
        </section>

        <footer className="memetic-spider-footer">
          <p>A live map of how entities connect across independent news sources.</p>
          <div className="memetic-spider-actions">
            <Link to="/desk" className="btn btn-outline-primary btn-sm">Live desk</Link>
            <Link to="/feed" className="btn btn-outline-primary btn-sm">Browse feed</Link>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default MemeticSpiderPage;
